import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  BreadcrumbArray,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  CurrencyFlagEmoji,
  DateUI,
  LabelPill,
  NumericFormatInput,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Spinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  currencyCodeToSingularName,
  currencySchema,
  fxAllowedCurrencies,
  getCountryLists,
  NotFoundError,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, Link } from "@tanstack/react-router";
import Decimal from "decimal.js";
import countries from "i18n-iso-countries";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { EntityScreeningCard } from "~/components/EntityScreeningCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { currencyValues } from "~/gql/client/enumValues";
import type {
  AdminAvailableCurrencycloudAccountFragment,
  AdminBusinessEntityCurrencycloudAccountFragment,
  AdminBusinessEntityOrganisationFragment,
  BusinessEntityDetailQuery,
  Currency,
  Environment,
  SetBusinessEntityAddressInput,
  UpdateBusinessEntityDetailInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/organisations_/$organisationId_/business-entities_/$businessEntityId",
)({
  head: () => opsHead("Business Entity"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const creditConditionsSchema = z.object({
  notionalAmount: z
    .number()
    .min(0.01, "Notional amount must be greater than 0")
    .transform((value) => new Decimal(value)),
  notionalCurrency: currencySchema,
});

type CreditConditionsFormValues = {
  notionalAmount: number;
  notionalCurrency: Currency;
};

const setEburyClientCreditConditionsMutation = graphql(`
  mutation SetEburyClientCreditConditions($input: SetEburyClientCreditConditionsInput!) {
    setEburyClientCreditConditions(input: $input) {
      eburyClient {
        id
        creditConditions {
          notionalAmount
          notionalCurrency
        }
      }
    }
  }
`);

type EburyClientForCreditConditions = NonNullable<
  NonNullable<BusinessEntityDetailQuery["businessEntity"]>["eburyClient"]
>;

function EburyClientCreditConditionsForm({
  eburyClient,
  showClientId,
}: {
  eburyClient: EburyClientForCreditConditions;
  showClientId: boolean;
}) {
  const existingCreditConditions = eburyClient.creditConditions;
  const defaultValues = useMemo(
    () => ({
      notionalAmount: existingCreditConditions
        ? existingCreditConditions.notionalAmount.toNumber()
        : 0,
      notionalCurrency: existingCreditConditions?.notionalCurrency ?? "GBP",
    }),
    [existingCreditConditions],
  );

  const [setEburyClientCreditConditions, setEburyClientCreditConditionsResult] =
    useMutation(setEburyClientCreditConditionsMutation, {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Ebury credit conditions updated",
        });
      },
    });

  const creditConditionsForm = useAppForm({
    defaultValues: defaultValues as CreditConditionsFormValues,
    validators: {
      onSubmit: creditConditionsSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = creditConditionsSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Invalid credit conditions",
        });
        return;
      }

      try {
        await setEburyClientCreditConditions({
          variables: {
            input: {
              eburyClientId: eburyClient.id,
              notionalAmount: parsed.data.notionalAmount,
              notionalCurrency: parsed.data.notionalCurrency,
            },
          },
        });
      } catch (error) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update Ebury credit conditions",
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
  });

  return (
    <div>
      <h4 className="mb-2 font-medium text-base">
        Credit Conditions{showClientId ? ` (${eburyClient.id})` : ""}
      </h4>
      <creditConditionsForm.AppForm>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void creditConditionsForm.handleSubmit();
          }}
        >
          <input type="hidden" name="eburyClientId" value={eburyClient.id} />
          <div className="flex flex-col gap-4">
            <div className="grid w-1/2 grid-cols-2 gap-4">
              <creditConditionsForm.AppField name="notionalAmount">
                {(field) => (
                  <div>
                    <span>Notional amount</span>
                    <NumericFormatInput
                      value={field.state.value}
                      onChange={(value) =>
                        value.target.value &&
                        field.handleChange(value.target.value.toNumber())
                      }
                      required
                      thousandSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="mt-1 text-danger text-sm">
                        {field.state.meta.errors
                          .map((err) => err?.message)
                          .join(", ")}
                      </p>
                    )}
                  </div>
                )}
              </creditConditionsForm.AppField>

              <creditConditionsForm.AppField name="notionalCurrency">
                {(field) => (
                  <field.SelectField
                    label="Notional currency"
                    errorVisibilityMode="onBlur"
                  >
                    {currencyValues.map((currency) => (
                      <field.SelectItem key={currency} value={currency}>
                        <div className="flex flex-row gap-2">
                          <CurrencyFlagEmoji
                            currency={currency}
                            className="rounded text-lg"
                          />
                          {currency}
                        </div>
                      </field.SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </creditConditionsForm.AppField>
            </div>

            {setEburyClientCreditConditionsResult.error && (
              <Alert
                kind="error"
                description={setEburyClientCreditConditionsResult.error.message}
              />
            )}

            <div className="flex justify-end">
              <Button
                disabled={setEburyClientCreditConditionsResult.loading}
                loading={setEburyClientCreditConditionsResult.loading}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </creditConditionsForm.AppForm>
    </div>
  );
}

function View() {
  const params = Route.useParams();
  const [eburyClientId, setEburyClientId] = useState("");
  const [visaAccountConfirmed, setVisaAccountConfirmed] = useState(false);

  const { data, error } = useSuspenseQuery(
    graphql(`
      query BusinessEntityDetail($id: ID!) {
        appEnvironment {
          id
          environment
        }
        businessEntity(id: $id) {
          id
          ...AdminBusinessEntityDetail
        }
        ... @defer {
          eburyClients {
            id
            clientName
            businessEntity {
              id
            }
          }
        }
        ... @defer {
          currencycloudAccounts {
            id
            ... AdminAvailableCurrencycloudAccount
          }
        }
      }

      fragment AdminBusinessEntityDetail on BusinessEntity {
        id
        name
        currency
        domiciledIn
        kycVerified
        netsuiteInternalCustomerId
        organisation {
          id
          ...AdminBusinessEntityOrganisation
        }
        eburyClient {
          id
          creditConditions {
            notionalAmount
            notionalCurrency
          }
        }
        address {
          id
          buildingNumber
          streetName
          city
          state
          postcode
          country
        }
        currencycloudAccount {
          id
          ...AdminBusinessEntityCurrencycloudAccount
        }
        tradeableCurrencies
        screeningResults {
          ...EntityScreeningCardFields
        }
      }

      fragment AdminBusinessEntityOrganisation on Organisation {
        id
        name
        activityStatementFormat
        memberships {
          id
          userProfile {
            id
            avatarUrl
            friendlyName
          }
        }
      }

      fragment AdminBusinessEntityCurrencycloudAccount on CurrencycloudAccount {
        id
        ... @defer {
          id
          accountName
          shortReference
          status
          createdAt
          defaultOnBehalfOfContact {
            id
            friendlyName
            userProfile {
              id
              friendlyName
            }
          }
        }
      }

      fragment AdminAvailableCurrencycloudAccount on CurrencycloudAccount {
        id
        accountName
        shortReference
        businessEntity {
          id
        }
      }
    `),
    { variables: { id: params.businessEntityId ?? "" } },
  );
  const businessEntity = data?.businessEntity;
  if (
    !businessEntity?.organisation ||
    businessEntity.organisation.id !== params.organisationId
  ) {
    throw new NotFoundError();
  }

  useOpsTitle(
    `${data.businessEntity?.organisation?.name} - ${data.businessEntity?.name}`,
  );

  useEffect(() => {
    if (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Error",
        description: error.message,
      });
    }
  }, [error]);

  const currencycloudAccountErrors = error?.graphQLErrors.filter(
    (g) =>
      g.path?.at(0) === "businessEntity" &&
      g.path?.at(1) === "currencycloudAccount",
  );

  const [updateDetails, updateDetailsResult] = useMutation(
    graphql(`
      mutation UpdateBusinessEntityDetail($input: UpdateBusinessEntityDetailInput!) {
        updateBusinessEntityDetail(input: $input) {
          businessEntity {
            id
            name
            currency
            domiciledIn
            kycVerified
            netsuiteInternalCustomerId
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Details updated",
        });
      },
    },
  );

  const [linkEburyClient, linkEburyClientResult] = useMutation(
    graphql(`
      mutation LinkEburyClient($input: LinkEburyClientInput!) {
        linkEburyClient(input: $input) {
          businessEntity {
            id
            eburyClient {
              id
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        setEburyClientId("");
      },
    },
  );

  const [setAddress, setAddressResult] = useMutation(
    graphql(`
      mutation SetBusinessEntityAddress($input: SetBusinessEntityAddressInput!) {
        setBusinessEntityAddress(input: $input) {
          businessEntity {
            id
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Business entity address updated",
        });
      },
    },
  );

  const [createCurrencycloudAccount, createCurrencycloudAccountResult] =
    useMutation(
      graphql(`
      mutation CreateCurrencycloudAccount($input: CreateCurrencycloudAccountInput!) {
        createCurrencycloudAccount(input: $input) {
          businessEntity {
            id
            currencycloudAccount {
              id
              ...AdminBusinessEntityCurrencycloudAccount
            }
          }
        }
      }
    `),
      {
        onCompleted: () => {
          toast({
            kind: "success",
            autoHide: true,
            title: "Visa account created",
          });
        },
      },
    );

  const { commonCountriesList, uncommonCountriesList } = getCountryLists();

  // Remix will not remount the component when navigating between business entities. Manually reset fields on change.
  const previousBusinessEntityId = useRef(params.businessEntityId);
  useEffect(() => {
    if (previousBusinessEntityId.current !== params.businessEntityId) {
      setEburyClientId("");
      setVisaAccountConfirmed(false);
      linkEburyClientResult.reset?.();
      createCurrencycloudAccountResult.reset?.();
      previousBusinessEntityId.current = params.businessEntityId;
    }
  });

  const requiresNetSuiteCustomerId =
    businessEntity.organisation?.activityStatementFormat === "NETSUITE";
  const detailsForm = useAppForm({
    defaultValues: {
      name: data.businessEntity?.name ?? "",
      currency: data.businessEntity?.currency ?? undefined,
      domiciledIn: data.businessEntity?.domiciledIn,
      kycVerified: data.businessEntity?.kycVerified ?? false,
      netsuiteInternalCustomerId:
        data.businessEntity?.netsuiteInternalCustomerId ?? "",
    } as Omit<UpdateBusinessEntityDetailInput, "businessEntityId">,
    validators: {
      onSubmit: z
        .object({
          name: z.string().min(1),
          currency: currencySchema.optional(),
          domiciledIn: z.string().nullish(),
          kycVerified: z.boolean().nullish(),
          netsuiteInternalCustomerId: z.string().optional(),
        })
        .superRefine((value, ctx) => {
          if (
            requiresNetSuiteCustomerId &&
            !value.netsuiteInternalCustomerId?.trim()
          ) {
            ctx.addIssue({
              path: ["netsuiteInternalCustomerId"],
              code: z.ZodIssueCode.custom,
              message:
                "This organisation is configured to use NetSuite activity statements. A NetSuite internal customer ID is required for all business entities.",
            });
          }
        }),
    },
    onSubmit: async ({ value: formFields }) => {
      updateDetails({
        variables: {
          input: {
            businessEntityId: businessEntity.id,
            ...formFields,
            currency: formFields.currency || undefined,
            domiciledIn: formFields.domiciledIn,
            netsuiteInternalCustomerId:
              formFields.netsuiteInternalCustomerId?.trim(),
          },
        },
      });
    },
  });

  const addressForm = useAppForm({
    defaultValues: {
      buildingNumber: data.businessEntity?.address?.buildingNumber ?? "",
      streetName: data.businessEntity?.address?.streetName ?? "",
      city: data.businessEntity?.address?.city ?? "",
      state: data.businessEntity?.address?.state ?? "",
      postcode: data.businessEntity?.address?.postcode ?? "",
      country:
        [...commonCountriesList, ...uncommonCountriesList].find(
          ({ label }) => label === data.businessEntity?.address?.country,
        )?.value ?? "AUS",
    } as Omit<SetBusinessEntityAddressInput, "businessEntityId">,
    validators: {
      onSubmit: z.object({
        buildingNumber: z.string(),
        streetName: z.string(),
        city: z.string(),
        state: z.string(),
        postcode: z.string(),
        country: z.string().refine(countries.isValid),
      }),
    },
    onSubmit: async ({ value: formFields }) => {
      setAddress({
        variables: {
          input: {
            businessEntityId: businessEntity.id,
            ...formFields,
          },
        },
      });
    },
  });

  const handleAddEburyClient = () => {
    if (!eburyClientId.trim()) {
      throw new Error("Ebury Client ID is required");
    }
    linkEburyClient({
      variables: {
        input: {
          businessEntityId: businessEntity.id,
          eburyClientId: eburyClientId.trim(),
        },
      },
    });
  };

  const handleCreateCurrencycloudAccount = () => {
    createCurrencycloudAccount({
      variables: {
        input: {
          businessEntityId: businessEntity.id,
        },
      },
    });
  };

  const entityCurrencycloudAccount =
    data.businessEntity?.currencycloudAccount ?? null;

  const entityEburyClient = data.businessEntity?.eburyClient ?? null;

  const unlinkedEburyClients = (data.eburyClients ?? []).filter(
    (e) => e.businessEntity === null,
  );

  return (
    <AdminPageContainer>
      <BreadcrumbArray
        stack={[
          {
            to: "/organisations",
            title: "Organisations",
          },
          {
            to: `/organisations/${data?.businessEntity?.organisation?.id}`,
            title: data.businessEntity?.organisation?.name,
          },
          {
            title: data?.businessEntity?.name,
          },
        ]}
      />
      <AdminPageHeader
        title={`${data.businessEntity?.organisation?.name} - ${data.businessEntity?.name}`}
      />
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="mb-4">
              <Txt variant="section-title">Business Entity</Txt>
            </div>
            <div className="rounded-lg border bg-white p-4 text-sm shadow-surface">
              <detailsForm.AppForm>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void detailsForm.handleSubmit();
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <detailsForm.AppField name="name">
                      {(field) => <field.InputField label="Name" />}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="currency">
                      {(field) => (
                        <field.SelectField label="Currency">
                          {currencyValues.map((c) => (
                            <field.SelectItem value={c} key={c}>
                              <div className="flex flex-row gap-2">
                                <CurrencyFlagEmoji
                                  currency={c}
                                  className="rounded text-lg"
                                />
                                {c}
                              </div>
                            </field.SelectItem>
                          ))}
                        </field.SelectField>
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="domiciledIn">
                      {(field) => (
                        <field.SelectField label="Domiciled In">
                          {Object.entries(countries.getNames("en"))
                            .sort((a, b) => a[1].localeCompare(b[1]))
                            .map(([alpha2Code, friendlyCountry]) => (
                              <field.SelectItem
                                value={countries.toAlpha3(alpha2Code) ?? ""}
                                key={countries.toAlpha3(alpha2Code)}
                              >
                                {friendlyCountry}
                              </field.SelectItem>
                            ))}
                        </field.SelectField>
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="netsuiteInternalCustomerId">
                      {(field) => (
                        <field.InputField
                          label="NetSuite internal customer ID"
                          placeholder={
                            requiresNetSuiteCustomerId
                              ? "Required for this organisation"
                              : undefined
                          }
                        />
                      )}
                    </detailsForm.AppField>

                    <detailsForm.AppField name="kycVerified">
                      {(field) => <field.SwitchField label="KYC verified" />}
                    </detailsForm.AppField>

                    <div className="flex flex-col items-end">
                      <Button disabled={updateDetailsResult.loading}>
                        Save
                      </Button>
                    </div>
                  </div>
                </form>
              </detailsForm.AppForm>
            </div>
          </div>

          <div>
            <div className="mb-4">
              <Txt variant="section-title">Address</Txt>
            </div>
            <div className="rounded-lg border bg-white p-4 text-sm shadow-surface">
              <addressForm.AppForm>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void addressForm.handleSubmit();
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                      <addressForm.AppField name="buildingNumber">
                        {(field) => (
                          <field.InputField label="Building number" />
                        )}
                      </addressForm.AppField>
                      <addressForm.AppField name="streetName">
                        {(field) => <field.InputField label="Street name" />}
                      </addressForm.AppField>
                      <addressForm.AppField name="city">
                        {(field) => <field.InputField label="City" />}
                      </addressForm.AppField>
                    </div>

                    <div className="flex gap-4">
                      <addressForm.AppField name="state">
                        {(field) => <field.InputField label="State" />}
                      </addressForm.AppField>
                      <addressForm.AppField name="postcode">
                        {(field) => <field.InputField label="Postcode" />}
                      </addressForm.AppField>
                      <addressForm.AppField name="country">
                        {(field) => (
                          <field.SelectField
                            label="Country"
                            placeholder="Select a country"
                          >
                            {commonCountriesList.map(({ value, label }) => (
                              <SelectItem value={value} key={`common-${value}`}>
                                {label}
                              </SelectItem>
                            ))}
                            <SelectSeparator />
                            {uncommonCountriesList.map(({ value, label }) => (
                              <SelectItem
                                value={value}
                                key={`uncommon-${value}`}
                              >
                                {label}
                              </SelectItem>
                            ))}
                          </field.SelectField>
                        )}
                      </addressForm.AppField>
                    </div>
                    <div className="flex flex-col items-end">
                      <Button disabled={setAddressResult.loading}>Save</Button>
                    </div>
                  </div>
                </form>
              </addressForm.AppForm>
            </div>
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 gap-8">
          <div>
            <div className="mb-4">
              <Txt variant="section-title">Ebury</Txt>
            </div>
            <div className="rounded-lg border bg-white p-4 text-sm shadow-surface">
              {entityEburyClient != null ? (
                <div>
                  <p className="mb-2 text-primary">
                    The following Ebury client ID is associated with this
                    business entity:
                  </p>
                  <div className="rounded border bg-neutral-muted p-3">
                    <Txt variant="mono">{entityEburyClient.id}</Txt>
                  </div>
                </div>
              ) : (
                <p className="text-secondary italic">
                  No Ebury client connections configured
                </p>
              )}

              {entityEburyClient != null && (
                <div className="mt-4 border-grid border-t pt-4">
                  <EburyClientCreditConditionsForm
                    eburyClient={entityEburyClient}
                    showClientId={false}
                  />
                </div>
              )}

              {entityEburyClient == null && (
                <div className="mt-4 border-grid border-t pt-4">
                  <h4 className="mb-2 font-medium">Add Ebury Client</h4>
                  <div className="flex items-end gap-2">
                    <div className="flex flex-grow flex-col gap-4">
                      {linkEburyClientResult.error && (
                        <Alert
                          kind="error"
                          description={linkEburyClientResult.error.message}
                          className="mt-2"
                        />
                      )}

                      {data.eburyClients === undefined ? (
                        <div className="flex items-center gap-2 text-xs">
                          <Spinner size="md" />
                          Loading available Ebury clients
                        </div>
                      ) : (
                        <Select
                          disabled={
                            unlinkedEburyClients.length === 0 ||
                            linkEburyClientResult.loading
                          }
                          onValueChange={setEburyClientId}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                unlinkedEburyClients.length
                                  ? "Select Ebury client"
                                  : "No available Ebury clients to link"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {unlinkedEburyClients.map((eburyClient) => (
                              <SelectItem
                                key={eburyClient.id}
                                value={eburyClient.id}
                              >
                                {eburyClient.id} - {eburyClient.clientName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <Button
                      onClick={handleAddEburyClient}
                      disabled={
                        linkEburyClientResult.loading || !eburyClientId.trim()
                      }
                      loading={linkEburyClientResult.loading}
                      className="mb-[1px]"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="mb-4">
              <Txt variant="section-title">Visa accounts</Txt>
            </div>

            {entityCurrencycloudAccount !== null && (
              <CurrencycloudAccountCard
                account={entityCurrencycloudAccount}
                organisation={businessEntity.organisation}
                environment={data.appEnvironment.environment ?? undefined}
              />
            )}

            {entityCurrencycloudAccount !== null && (
              <CurrencycloudAssetSelectionCard
                tradeableCurrencies={businessEntity.tradeableCurrencies ?? []}
                businessEntityId={businessEntity.id}
              />
            )}

            {!!currencycloudAccountErrors?.length && (
              <Alert
                kind="error"
                description={
                  "Could not load Visa accounts: " +
                  Array.from(
                    new Set(currencycloudAccountErrors.map((c) => c.message)),
                  ).join(", ")
                }
                className="mt-2"
              />
            )}

            {!currencycloudAccountErrors?.length &&
              entityCurrencycloudAccount === null && (
                <div className="grid grid-cols-2 gap-8">
                  <div className="flex flex-col gap-4 rounded-lg border bg-white p-4 text-sm shadow-surface">
                    <div className="font-semibold">Open new Visa account</div>

                    <div className="flex gap-4">
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={visaAccountConfirmed}
                          onCheckedChange={(checked) =>
                            setVisaAccountConfirmed(!!checked)
                          }
                        />
                        <div>
                          I confirm that all required KYC (Know Your Customer),
                          AML (Anti-Money Laundering), and beneficial ownership
                          disclosure requirements have been satisfied for
                          opening a Visa business account for this entity, and I
                          have the authority to make this certification on
                          behalf of the business.
                        </div>
                      </div>
                      <div>
                        <Button
                          onClick={handleCreateCurrencycloudAccount}
                          disabled={
                            !visaAccountConfirmed ||
                            createCurrencycloudAccountResult.loading
                          }
                          loading={createCurrencycloudAccountResult.loading}
                        >
                          Open account
                        </Button>
                      </div>
                    </div>
                    {createCurrencycloudAccountResult.error && (
                      <Alert
                        kind="error"
                        description={
                          createCurrencycloudAccountResult.error.message
                        }
                      />
                    )}
                  </div>

                  <CurrencycloudLinkSection
                    businessEntityId={businessEntity.id}
                    currencycloudAccounts={data.currencycloudAccounts}
                  />
                </div>
              )}
          </div>
        </div>

        <EntityScreeningCard
          screeningResults={data.businessEntity?.screeningResults ?? []}
          entityId={{ businessEntityId: params.businessEntityId }}
          entityName={data.businessEntity?.name ?? ""}
        />
      </div>
    </AdminPageContainer>
  );
}

type OrganisationMembership = NonNullable<
  AdminBusinessEntityOrganisationFragment["memberships"]
>[number];

function CurrencycloudAccountCard({
  account,
  organisation,
  environment,
}: {
  account: AdminBusinessEntityCurrencycloudAccountFragment;
  organisation?: AdminBusinessEntityOrganisationFragment | null;
  environment?: Environment;
}) {
  const [
    setCurrencycloudAccountDefaultContact,
    setCurrencycloudAccountDefaultContactResult,
  ] = useMutation(
    graphql(
      `mutation AdminBusinessEntitySetCurrencycloudAccountDefaultContact($input: SetCurrencycloudAccountDefaultContactInput!) {
        setCurrencycloudAccountDefaultContact(input: $input) {
          currencycloudAccount {
            id
            ...AdminBusinessEntityCurrencycloudAccount
          }
        }
      }`,
    ),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Visa account updated",
        });
      },
      onError: (error) => {
        const description =
          error.graphQLErrors[0].message ??
          "Something went wrong. Please try again later.";
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update Visa account",
          description,
        });
      },
    },
  );

  const defaultOnBehalfOfUserProfile =
    account.defaultOnBehalfOfContact?.userProfile;

  const [selectedUserProfileId, setSelectedUserProfileId] = useState(
    defaultOnBehalfOfUserProfile?.id ?? null,
  );

  useEffect(() => {
    if (account.defaultOnBehalfOfContact !== undefined) {
      setSelectedUserProfileId(defaultOnBehalfOfUserProfile?.id ?? null);
    }
  }, [account.defaultOnBehalfOfContact, defaultOnBehalfOfUserProfile?.id]);

  const isUnchanged =
    selectedUserProfileId === (defaultOnBehalfOfUserProfile?.id ?? null);

  const organisationMemberships = organisation?.memberships;
  const contactOptions = useMemo(() => {
    const orgUsers = [...(organisationMemberships ?? [])]
      .filter(
        (
          v,
        ): v is OrganisationMembership & {
          userProfile: NonNullable<OrganisationMembership["userProfile"]>;
        } => !!v.userProfile,
      )
      .map((v) => v.userProfile)
      .sort((a, b) => {
        const nameA = a.friendlyName ?? "";
        const nameB = b.friendlyName ?? "";
        return nameA.localeCompare(nameB);
      });
    if (!orgUsers.length && environment === "DEVELOPMENT") {
      return [
        {
          id: "test",
          friendlyName: "Test user",
          avatarUrl: null,
        },
      ];
    }

    return orgUsers;
  }, [organisationMemberships, environment]);

  const handleSave = () => {
    setCurrencycloudAccountDefaultContact({
      variables: {
        input: {
          currencycloudAccountId: account.id,
          userProfileId: selectedUserProfileId,
        },
      },
    });
  };

  return (
    <Card className="border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link
            to={`/currencycloud/accounts/${account.id}`}
            className="font-medium text-lg text-primary-interactive underline"
          >
            {account.accountName ?? account.shortReference ?? account.id}
          </Link>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {!account.defaultOnBehalfOfContact && (
          <Alert
            kind="warning"
            description="A contact must be assigned before this account can be used for transactions. Per Visa's requirements, each account must have an authorized contact to process payments."
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Account ID</Txt>
            <Txt variant="mono">{account.id}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Ref</Txt>
            <Txt variant="mono">{account.shortReference ?? "Loading..."}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Account Name</Txt>
            <Txt variant="body">{account.accountName ?? "Loading..."}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Status</Txt>
            <Txt variant="body">{account.status ?? "Loading..."}</Txt>
          </div>

          <div className="flex flex-col gap-2">
            <Txt variant="detail-label">Created</Txt>
            <Txt variant="body">
              {account.createdAt ? (
                <DateUI date={new Date(account.createdAt)} />
              ) : (
                "Loading..."
              )}
            </Txt>
          </div>

          {organisation && (
            <div className="flex flex-col gap-2">
              <Txt variant="detail-label">Contact</Txt>

              <div className="min-w-[300px]">
                {account.defaultOnBehalfOfContact === undefined ? (
                  "Loading..."
                ) : (
                  <Select
                    value={selectedUserProfileId ?? undefined}
                    onValueChange={(value) => setSelectedUserProfileId(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select contact…" />
                    </SelectTrigger>

                    <SelectContent>
                      {contactOptions.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          <div className="flex items-center gap-2">
                            {m.avatarUrl && (
                              <img
                                src={m.avatarUrl}
                                alt={m.friendlyName ?? undefined}
                                className="h-5 w-5 rounded-full"
                              />
                            )}
                            <Txt variant="body">{m.friendlyName}</Txt>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex justify-end">
        <Button
          disabled={
            isUnchanged || setCurrencycloudAccountDefaultContactResult.loading
          }
          loading={setCurrencycloudAccountDefaultContactResult.loading}
          onClick={handleSave}
        >
          Save
        </Button>
      </CardFooter>
    </Card>
  );
}

const createCurrencycloudAssetsSchema = z.object({
  currencies: z
    .array(currencySchema)
    .min(1, "Please select at least one currency"),
});

type CreateCurrenycloudAssetsFormData = z.infer<
  typeof createCurrencycloudAssetsSchema
>;

function CurrencycloudAssetSelectionCard({
  tradeableCurrencies,
  businessEntityId,
}: {
  tradeableCurrencies: Currency[];
  businessEntityId: string;
}) {
  const [createCurrencycloudAssets] = useMutation(
    graphql(`
      mutation AdminCreateCurrencycloudAssets($input: CreateCurrencycloudAssetsInput!) {
        createCurrencycloudAssets(input: $input) {
          businessEntity {
            id
            ...AdminBusinessEntityDetail
          }
        }
      }
    `),
  );

  const form = useAppForm({
    defaultValues: {
      currencies: [] as Currency[],
    } as CreateCurrenycloudAssetsFormData,
    validators: {
      onSubmit: createCurrencycloudAssetsSchema,
      onChange: createCurrencycloudAssetsSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = createCurrencycloudAssetsSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating Visa FX assets",
        });
        return;
      }

      try {
        await createCurrencycloudAssets({
          variables: {
            input: {
              currencies: parsed.data.currencies,
              businessEntityId,
            },
          },
        });

        toast({
          kind: "success",
          autoHide: true,
          title: "Visa FX assets created successfully",
        });
        form.reset();
      } catch (error) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to create Visa FX assets",
          description:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
      }
    },
  });

  const tradeableCurrenciesSet = new Set(tradeableCurrencies);

  const selectedCurrencies = useStore(form.store, (state) => {
    return state.values.currencies;
  });

  return (
    <Card className="border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          Visa FX assets
        </CardTitle>
      </CardHeader>

      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppField name="currencies">
            {(field) => (
              <div className="flex flex-wrap p-2">
                {fxAllowedCurrencies.map((currency) => {
                  const alreadyExists = tradeableCurrenciesSet.has(currency);

                  const isSelected =
                    field.state.value?.includes(currency) || alreadyExists;

                  return (
                    <div
                      key={currency}
                      className="flex w-1/2 items-center gap-4 p-1 pt-2 pr-4 pb-2 pl-4"
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={alreadyExists}
                        onCheckedChange={() => {
                          const current = field.state.value || [];
                          if (current.includes(currency)) {
                            field.handleChange(
                              current.filter((c) => c !== currency),
                            );
                          } else {
                            field.handleChange([...current, currency]);
                          }
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <CurrencyFlagEmoji
                          className="h-4 rounded-xs shadow-surface"
                          currency={currency}
                        />
                        <div className="flex min-h-8 items-center">
                          <span className="text-sm">
                            {currencyCodeToSingularName[currency]} ({currency})
                          </span>
                        </div>
                      </div>

                      {alreadyExists && (
                        <LabelPill variant="success" label="Account open" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </form.AppField>

          <CardFooter className="mt-4 flex justify-end">
            <form.Subscribe
              selector={(state) => ({ isSubmitting: state.isSubmitting })}
            >
              {({ isSubmitting }) => (
                <div>
                  <Button
                    variant="primary"
                    disabled={isSubmitting || selectedCurrencies.length === 0}
                    loading={isSubmitting}
                    type="submit"
                  >
                    Open Visa FX assets
                  </Button>
                </div>
              )}
            </form.Subscribe>
          </CardFooter>
        </form>
      </form.AppForm>
    </Card>
  );
}

function CurrencycloudLinkSection({
  businessEntityId,
  currencycloudAccounts,
}: {
  businessEntityId: string;
  currencycloudAccounts:
    | AdminAvailableCurrencycloudAccountFragment[]
    | undefined
    | null;
}) {
  const unlinkedCurrencycloudAccounts = (currencycloudAccounts ?? []).filter(
    (e) => e.businessEntity === null,
  );

  const [currencycloudAccountId, setCurrencycloudAccountId] = useState("");

  const [linkCurrencycloudAccount, linkCurrencycloudAccountResult] =
    useMutation(
      graphql(`
      mutation LinkCurrencycloudAccount($input: LinkCurrencycloudAccountInput!) {
        linkCurrencycloudAccount(input: $input) {
          businessEntity {
            id
            ...AdminBusinessEntityDetail
          }
        }
      }
    `),
      {
        onCompleted: () => {
          setCurrencycloudAccountId("");
          toast({
            kind: "success",
            autoHide: true,
            title: "Visa account linked successfully",
          });
        },
        onError: (error) => {
          const description =
            error.graphQLErrors[0].message ??
            "Something went wrong. Please try again later.";
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to link Visa account",
            description,
          });
        },
      },
    );

  const handleAdd = () => {
    if (!currencycloudAccountId.trim()) {
      throw new Error("Ebury Client ID is required");
    }
    linkCurrencycloudAccount({
      variables: {
        input: {
          businessEntityId,
          currencycloudAccountId: currencycloudAccountId.trim(),
        },
      },
    });
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-white p-4 text-sm shadow-surface">
      <div className="font-semibold">Link existing Visa account</div>

      {currencycloudAccounts === undefined ? (
        <div className="flex items-center gap-2 text-xs">
          <Spinner size="md" />
          Loading available Visa accounts
        </div>
      ) : (
        <div className="flex gap-4">
          <Select
            disabled={
              unlinkedCurrencycloudAccounts.length === 0 ||
              linkCurrencycloudAccountResult.loading
            }
            onValueChange={setCurrencycloudAccountId}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  unlinkedCurrencycloudAccounts.length
                    ? "Select Visa account"
                    : "No available Visa accounts to link"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {unlinkedCurrencycloudAccounts.map((acc) => (
                <SelectItem key={acc.id} value={acc.id}>
                  {acc.shortReference} - {acc.accountName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            onClick={handleAdd}
            disabled={
              linkCurrencycloudAccountResult.loading ||
              !currencycloudAccountId.trim()
            }
            loading={linkCurrencycloudAccountResult.loading}
            className="mb-[1px]"
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}
